<template>
  <div v-if="user.isAnalytics()" class="analytics-view">
    <!-- Analytics Grid -->
    <div class="analytics-grid">
      <!-- Loading State -->
      <!-- Remove the overlay, only show error overlay if needed -->
      
      <!-- Analytics Content -->
      <div class="analytics-content">
        <!-- Error State -->
        <div v-if="error" class="error-overlay">
          <p class="error-message">{{ error }}</p>
          <button @click="fetchUptimeData" class="retry-button" data-testid="analytics-retry">Retry</button>
        </div>
        <!-- Key Metrics Row -->
        <div class="metrics-row">
          <div class="metric-card date-picker-card">
            <h3>Date Range</h3>
            <div class="date-controls">
              <div class="date-input-group">
                <label for="startDate">Start:</label>
                <input
                  id="startDate"
                  type="date"
                  data-testid="analytics-start-date"
                  v-model="startDate"
                  :max="endDate"
                  @change="fetchAllData"
                />
              </div>
              
              <div class="date-input-group">
                <label for="endDate">End:</label>
                <input
                  id="endDate"
                  type="date"
                  data-testid="analytics-end-date"
                  v-model="endDate"
                  :min="startDate"
                  :max="endDate"
                  @change="fetchAllData"
                />
              </div>
              
              <button 
                class="fetch-button"
                data-testid="analytics-update"
                @click="fetchAllData"
                :disabled="isLoading || isLoadingMetrics"
              >
                <span v-if="isLoading || isLoadingMetrics" class="inline-spinner"></span>
                <span v-else>Update</span>
              </button>
            </div>
          </div>
          <div class="metric-card">
            <h3>Alerts</h3>
            <div class="metric-value" data-testid="analytics-alerts-count">{{ alertsCount }}</div>
          </div>
          <div class="metric-card">
            <h3>Players</h3>
            <div class="metric-value" data-testid="analytics-players-count">{{ uniquePlayers || 'No data' }}</div>
          </div>
        </div>

        <!-- Uptime Charts - Show independently of metrics -->
        <div class="charts-row">
          <!-- Uptime Chart -->
          <div class="chart-card">
            <div v-if="uptimeData.length > 0" class="chart-container">
              <UptimeChart
                :uptime-data="uptimeData"
                :start-date="startDateObj"
                :end-date="endDateObj"
              />
            </div>
            <div v-else class="no-data-container">
              <div class="no-data-icon">📊</div>
              <p>No uptime data available for the selected date range</p>
              <small v-if="error">{{ error }}</small>
            </div>
          </div>
          <!-- Recently Offline Devices -->
          <div class="chart-card">
            <div class="chart-header">
              <h3>Recently Offline Devices</h3>
              <p>Based on selected date range (last day)</p>
            </div>
            <div class="list-card-container">
              <div v-if="uptimeData.length === 0" class="no-offline">No uptime data available for offline device analysis.</div>
              <div v-else-if="recentOfflineDevices.length === 0" class="no-offline">No recently offline devices.</div>
              <div v-else class="offline-list-scroll">
                <ul class="offline-list">
                  <li v-for="device in recentOfflineDevices" :key="device.playerId">
                    <a :href="`/players/edit/${device.playerId}`" target="_blank" class="offline-link">{{ device.playerName || `Player ${device.playerId}` }}</a>
                    <span class="offline-meta">Last seen: {{ device.lastSeenDate }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
          <!-- System Metrics Heatmaps -->
          <div class="charts-row">
            <div class="chart-card">
              <div class="chart-container">
                <HeatmapChart
                  :data="memHeatmapData"
                  metric-type="mem"
                  :is-loading="isLoadingMetrics"
                  :error="metricsError"
                  :start-date="startDateObj"
                  :end-date="endDateObj"
                  :player-records="heatmapPlayerRecords"
                  :interval="currentInterval"
                  @retry="fetchMetricsData"
                />
              </div>
            </div>
            <div class="chart-card">
              <div class="chart-container">
                <HeatmapChart
                  :data="cpuHeatmapData"
                  metric-type="cpu"
                  :is-loading="isLoadingMetrics"
                  :error="metricsError"
                  :start-date="startDateObj"
                  :end-date="endDateObj"
                  :player-records="heatmapPlayerRecords"
                  :interval="currentInterval"
                  @retry="fetchMetricsData"
                />
              </div>
            </div>
          </div>
          <div class="charts-row">
            <div class="chart-card">
              <div class="chart-container">
                <HeatmapChart
                  :data="diskHeatmapData"
                  metric-type="disk"
                  :is-loading="isLoadingMetrics"
                  :error="metricsError"
                  :start-date="startDateObj"
                  :end-date="endDateObj"
                  :player-records="heatmapPlayerRecords"
                  :interval="currentInterval"
                  @retry="fetchMetricsData"
                />
              </div>
            </div>
            <div class="chart-card">
              <div class="chart-container">
                <HeatmapChart
                  :data="tempHeatmapData"
                  metric-type="temp"
                  :is-loading="isLoadingMetrics"
                  :error="metricsError"
                  :start-date="startDateObj"
                  :end-date="endDateObj"
                  :player-records="heatmapPlayerRecords"
                  :interval="currentInterval"
                  @retry="fetchMetricsData"
                />
              </div>
            </div>
          </div>
        
        <!-- Individual charts handle their own no-data states -->
        <!-- Debug info for development -->
        <div v-if="metricsResponse" class="debug-info" style="padding: 1rem; background: #f3f4f6; margin: 1rem 0; border-radius: 8px; font-size: 0.8rem;">
          <strong>📊 Metrics API Status:</strong>
          Interval: {{ metricsResponse.interval }} | 
          Total Buckets: {{ metricsResponse.totalBuckets }} | 
          Data Points: {{ aggregatedMetricsData.length }} |
          Devices: {{ [...new Set(aggregatedMetricsData.map(d => d.player_uuid))].length }}
        </div>
      </div>
    </div>
  </div>
  <div v-else class="access-denied">
    <h2>Access Denied</h2>
    <p>You don't have permission to view analytics data.</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { getPlayerUptimes, type UptimeData } from '@/helpers/api/player/uptimes';
import { getPlayersByDeviceUuids } from '@/helpers/api/player/player';
import { 
  getAggregatedMetrics, 
  transformAggregatedMetricsForHeatmap, 
  getOptimalInterval,
  type AggregatedMetricsData,
  type AggregatedMetricsResponse 
} from '@/helpers/api/metrics';
import UptimeChart from '@/components/UptimeChart/UptimeChart.vue';
import HeatmapChart from '@/components/HeatmapChart/HeatmapChart.vue';
import useUser from '@/composables/user';

const user = useUser();
const debugAnalytics = false; // set true for local debugging

const startDate = ref('');
const endDate = ref('');
const uptimeData = ref<UptimeData[]>([]);
const aggregatedMetricsData = ref<AggregatedMetricsData[]>([]);
const metricsResponse = ref<AggregatedMetricsResponse | null>(null);
const playerRecordsByUuid = ref<Record<string, { name: string; id?: number; deviceUuid?: string }>>({});
const isLoading = ref(false);
const isLoadingMetrics = ref(false);
const error = ref('');
const metricsError = ref('');

// Set default date range (last 7 days for optimal aggregated data display)
const setDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7); // 7 days - good balance for aggregated data
  
  endDate.value = end.toISOString().split('T')[0];
  startDate.value = start.toISOString().split('T')[0];
};

const startDateObj = computed(() => {
  const [year, month, day] = startDate.value.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
});
const endDateObj = computed(() => {
  const [year, month, day] = endDate.value.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
});

// Expose current interval for child charts to align positioning
const currentInterval = computed((): '1h' | '4h' | '6h' | '1d' => {
  const interval = metricsResponse.value?.interval;
  if (interval === '1h' || interval === '4h' || interval === '6h' || interval === '1d') {
    return interval;
  }
  return '1h'; // Default fallback
});

const uniquePlayers = computed(() => {
  const playerIds = new Set(uptimeData.value.map(record => record.playerId));
  return playerIds.size;
});

// Alerts: number of unique players with any HIGH metric (matches heatmap red thresholds)
const alertsCount = computed(() => {
  if (!aggregatedMetricsData.value || aggregatedMetricsData.value.length === 0) return 0;
  const playersWithHigh = new Set<string>();
  // Heatmap "high" thresholds per metric
  const isHigh = (record: AggregatedMetricsData) => {
    return (
      (record.avg_mem_usage_pct ?? 0) >= 80 ||
      (record.avg_cpu_usage_pct ?? 0) >= 70 ||
      (record.avg_disk_usage_pct ?? 0) >= 90 ||
      (record.avg_temperature_c ?? 0) >= 70
    );
  };
  for (const rec of aggregatedMetricsData.value) {
    if (isHigh(rec)) playersWithHigh.add(rec.player_uuid);
  }
  return playersWithHigh.size;
});

const recentOfflineDevices = computed(() => {
  if (!uptimeData.value || uptimeData.value.length === 0) return [] as Array<{playerId: number, playerName: string, lastSeenDate: string}>;

  // Build display date range (inclusive)
  const start = startDateObj.value;
  const end = endDateObj.value;
  if (!(start instanceof Date) || isNaN(start.getTime()) || !(end instanceof Date) || isNaN(end.getTime())) {
    return [] as Array<{playerId: number, playerName: string, lastSeenDate: string}>;
  }

  const displayDates: Date[] = [];
  const currentDate = new Date(start);
  while (currentDate <= end) {
    displayDates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  if (displayDates.length === 0) return [] as Array<{playerId: number, playerName: string, lastSeenDate: string}>;

  const extraDay = new Date(end);
  extraDay.setDate(extraDay.getDate() + 1);
  const extraDayStr = extraDay.toISOString().split('T')[0];

  // Initialize daily data map
  const dailyData: Record<string, Set<number>> = {};
  displayDates.forEach(date => {
    const dateStr = date.toISOString().split('T')[0];
    dailyData[dateStr] = new Set<number>();
  });
  // ensure extra day key exists
  dailyData[extraDayStr] = dailyData[extraDayStr] || new Set<number>();

  const playerIdToName: Record<number, string> = {};
  const playerIdToLastSeen: Record<number, number> = {};
  const devicesInRange = new Set<number>();

  uptimeData.value.forEach(record => {
    const pid = record.playerId;
    playerIdToName[pid] = record.playerName;

    const recStart = new Date(record.dateStarted);
    const recEnd = new Date(record.dateEnded);

    // Only consider devices that had activity within or before the selected date range
    if (recStart <= end) {
      devicesInRange.add(pid);
      
      // Track last seen (latest end time) only within the selected range
      const endTs = Math.min(recEnd.getTime(), end.getTime());
      if (!playerIdToLastSeen[pid] || endTs > playerIdToLastSeen[pid]) {
        playerIdToLastSeen[pid] = endTs;
      }
    }

    // Fill active days, capped at extra day
    const currentDate = new Date(recStart);
    while (currentDate <= recEnd && currentDate <= extraDay) {
      const dateString = currentDate.toISOString().split('T')[0];
      if (dailyData[dateString]) {
        dailyData[dateString].add(pid);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  // Determine offline devices for the last day in the selected range
  const lastDay = displayDates[displayDates.length - 1];
  const lastDayStr = lastDay.toISOString().split('T')[0];
  const onlineOnLastDay = dailyData[lastDayStr] || new Set<number>();
  const onlineOnExtraDay = dailyData[extraDayStr] || new Set<number>();

  const offlineIds: number[] = [];
  devicesInRange.forEach(id => {
    if (!onlineOnLastDay.has(id) && !onlineOnExtraDay.has(id)) {
      offlineIds.push(id);
    }
  });

  const items = offlineIds.map(id => {
    const lastSeen = playerIdToLastSeen[id];
    const lastSeenDate = lastSeen ? new Date(lastSeen).toLocaleString() : 'Unknown';
    return {
      playerId: id,
      playerName: playerIdToName[id] || `Player ${id}`,
      lastSeenDate,
    };
  });

  // Sort descending by last seen date (unknown at end)
  items.sort((a, b) => {
    const aTime = a.lastSeenDate === 'Unknown' ? 0 : new Date(a.lastSeenDate).getTime();
    const bTime = b.lastSeenDate === 'Unknown' ? 0 : new Date(b.lastSeenDate).getTime();
    return bTime - aTime;
  });

  return items;
});

// Helper function to normalize UUIDs for comparison (handle both with and without dashes)
const normalizeUuid = (uuid: string): string => {
  return uuid.toLowerCase().replace(/-/g, '');
};

// Fetch player records by device UUIDs from the API
const fetchPlayerRecordsByUuids = async (uuids: string[]) => {
  if (uuids.length === 0) {
    playerRecordsByUuid.value = {};
    return;
  }

  try {
    if (debugAnalytics) {
      console.log('🔍 Fetching player records for UUIDs:', uuids);
    }

    // Note: getPlayersByDeviceUuids strips hyphens from UUIDs before sending to API
    // because the main DSP database stores UUIDs without hyphens, while metrics table has them with hyphens
    const players = await getPlayersByDeviceUuids(uuids);
    
    // Build a map of UUID to player record
    // Use the original UUID from metrics data (with hyphens) as the key
    // This matches what the heatmap data uses
    const records: Record<string, { name: string; id?: number; deviceUuid?: string }> = {};
    
    // Create a map of normalized UUIDs from the API response for matching
    // API response deviceUuid might be with or without hyphens, so we normalize for comparison
    const playersByNormalizedUuid = new Map<string, typeof players[0]>();
    players.forEach(player => {
      if (player.deviceUuid) {
        const normalized = normalizeUuid(player.deviceUuid);
        playersByNormalizedUuid.set(normalized, player);
      }
    });

    // Match each UUID from metrics data (with hyphens) with players from API
    // Normalize both sides for comparison since formats might differ
    uuids.forEach(uuid => {
      const normalized = normalizeUuid(uuid);
      const player = playersByNormalizedUuid.get(normalized);
      
      if (player) {
        // Found a match - use the original UUID from metrics (with hyphens) as the key
        records[uuid] = {
          name: player.name,
          id: player.id,
          deviceUuid: player.deviceUuid
        };
      } else {
        // No match found - create fallback entry
        records[uuid] = {
          name: uuid.substring(0, 8) + '...',
          deviceUuid: uuid
        };
      }
    });

    playerRecordsByUuid.value = records;

    if (debugAnalytics) {
      console.log('✅ Fetched player records:', records);
      console.log(`  • Found ${players.length} players out of ${uuids.length} UUIDs`);
      console.log(`  • Matched ${Object.values(records).filter(r => r.id !== undefined).length} players with IDs`);
    }
  } catch (err: any) {
    console.error('❌ Error fetching player records by UUIDs:', err);
    // On error, still create fallback entries
    const records: Record<string, { name: string; id?: number; deviceUuid?: string }> = {};
    uuids.forEach(uuid => {
      records[uuid] = {
        name: uuid.substring(0, 8) + '...',
        deviceUuid: uuid
      };
    });
    playerRecordsByUuid.value = records;
  }
};

// Build playerRecords map for heatmap components
const heatmapPlayerRecords = computed(() => {
  return playerRecordsByUuid.value;
});

// Computed properties for heatmap data using aggregated metrics
const memHeatmapData = computed(() => {
  const result = transformAggregatedMetricsForHeatmap(aggregatedMetricsData.value, 'mem');
  console.log('Memory heatmap computed:', result.length, 'points');
  return result;
});
const cpuHeatmapData = computed(() => {
  const result = transformAggregatedMetricsForHeatmap(aggregatedMetricsData.value, 'cpu');
  console.log('CPU heatmap computed:', result.length, 'points');
  return result;
});
const diskHeatmapData = computed(() => {
  const result = transformAggregatedMetricsForHeatmap(aggregatedMetricsData.value, 'disk');
  console.log('Disk heatmap computed:', result.length, 'points');
  return result;
});
const tempHeatmapData = computed(() => {
  const result = transformAggregatedMetricsForHeatmap(aggregatedMetricsData.value, 'temp');
  console.log('Temp heatmap computed:', result.length, 'points');
  return result;
});

const fetchMetricsData = async () => {
  if (!startDate.value || !endDate.value) {
    metricsError.value = 'Please select both start and end dates';
    return;
  }

  isLoadingMetrics.value = true;
  metricsError.value = '';

  try {
    // Construct proper start and end times with timezone considerations
    // Use local timezone to avoid date shifting issues
    const startDateTime = new Date(startDate.value + 'T00:00:00');
    const endDateTime = new Date(endDate.value + 'T23:59:59');
    
    // Convert to UTC for API consistency
    const startTime = startDateTime.toISOString();
    const endTime = endDateTime.toISOString();
    
    if (debugAnalytics) {
      console.log('📅 Date conversion debug:', {
        inputStart: startDate.value,
        inputEnd: endDate.value,
        localStart: startDateTime.toString(),
        localEnd: endDateTime.toString(),
        utcStart: startTime,
        utcEnd: endTime
      });
    }
    
    // Determine interval: force 4h for single-day ranges, otherwise use heuristic
    const isSameDayRange = startDate.value === endDate.value;
    const interval = isSameDayRange ? '4h' : getOptimalInterval(startTime, endTime);
    const daysDifference = Math.ceil((new Date(endDate.value).getTime() - new Date(startDate.value).getTime()) / (1000 * 60 * 60 * 24));

    if (debugAnalytics) {
      console.log('🚀 Fetching aggregated metrics with optimized parameters:', { 
        startTime, 
        endTime, 
        interval,
        startDate: startDate.value, 
        endDate: endDate.value,
        daysDifference
      });
    }
    
    // Use the actual logged-in user's account ID
    const userAccountId = user.accountId;
    
    const response = await getAggregatedMetrics({
      accountId: userAccountId,
      startTime,
      endTime,
      interval
    });
    
    if (debugAnalytics) {
      console.log('✅ Successfully fetched aggregated metrics:', {
        totalBuckets: response.totalBuckets,
        interval: response.interval,
        dataPoints: response.data.length,
        filters: response.filters
      });
    }
    
    // Store the aggregated data
    metricsResponse.value = response;
    let finalData = response.data;
    
    // Handle current day for 1d intervals: if end date is today and we're using daily intervals,
    // fetch current day's partial data and aggregate it
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Parse endDate.value (YYYY-MM-DD) as local date to avoid timezone issues
    const [year, month, day] = endDate.value.split('-').map(Number);
    const endDateObj = new Date(year, month - 1, day); // month is 0-indexed
    endDateObj.setHours(0, 0, 0, 0);
    const isEndDateToday = endDateObj.getTime() === today.getTime();
    const isUsingDailyInterval = interval === '1d';
    
    if (isEndDateToday && isUsingDailyInterval) {
      // Check if we already have data for today
      // Compare dates using local date strings to avoid timezone issues
      const todayStr = endDate.value; // Already in YYYY-MM-DD format
      const hasTodayData = response.data.some(d => {
        const bucketDate = new Date(d.time_bucket);
        // Get local date string (YYYY-MM-DD) for comparison
        const bucketYear = bucketDate.getFullYear();
        const bucketMonth = String(bucketDate.getMonth() + 1).padStart(2, '0');
        const bucketDay = String(bucketDate.getDate()).padStart(2, '0');
        const bucketDateStr = `${bucketYear}-${bucketMonth}-${bucketDay}`;
        return bucketDateStr === todayStr;
      });
      
      if (!hasTodayData) {
        // Fetch today's data with a smaller interval to get partial day data
        const todayStart = new Date(endDate.value + 'T00:00:00');
        const now = new Date();
        const todayEnd = new Date(now);
        
        if (debugAnalytics) {
          console.log('📅 Current day missing, fetching partial day data:', {
            todayStart: todayStart.toISOString(),
            todayEnd: todayEnd.toISOString()
          });
        }
        
        try {
          // Use 1h interval for today to get granular data
          const todayResponse = await getAggregatedMetrics({
            accountId: userAccountId,
            startTime: todayStart.toISOString(),
            endTime: todayEnd.toISOString(),
            interval: '1h'
          });
          
          if (todayResponse.data.length > 0) {
            // Aggregate today's hourly data into a single daily bucket
            const todayDataByPlayer = new Map<string, {
              mem: number[];
              cpu: number[];
              disk: number[];
              temp: number[];
              dataPoints: number;
            }>();
            
            todayResponse.data.forEach(bucket => {
              if (!todayDataByPlayer.has(bucket.player_uuid)) {
                todayDataByPlayer.set(bucket.player_uuid, {
                  mem: [],
                  cpu: [],
                  disk: [],
                  temp: [],
                  dataPoints: 0
                });
              }
              
              const playerData = todayDataByPlayer.get(bucket.player_uuid)!;
              if (bucket.avg_mem_usage_pct != null) playerData.mem.push(bucket.avg_mem_usage_pct);
              if (bucket.avg_cpu_usage_pct != null) playerData.cpu.push(bucket.avg_cpu_usage_pct);
              if (bucket.avg_disk_usage_pct != null) playerData.disk.push(bucket.avg_disk_usage_pct);
              if (bucket.avg_temperature_c != null) playerData.temp.push(bucket.avg_temperature_c);
              playerData.dataPoints += bucket.data_points || 0;
            });
            
            // Create aggregated daily buckets for today
            const todayBuckets: typeof response.data = [];
            todayDataByPlayer.forEach((data, playerUuid) => {
              const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
              const max = (arr: number[]) => arr.length > 0 ? Math.max(...arr) : 0;
              
              // Use noon of today as the bucket timestamp (standard for daily buckets)
              const todayNoon = new Date(endDate.value + 'T12:00:00');
              
              todayBuckets.push({
                time_bucket: todayNoon.toISOString(),
                player_uuid: playerUuid,
                avg_mem_usage_pct: avg(data.mem),
                avg_cpu_usage_pct: avg(data.cpu),
                avg_disk_usage_pct: avg(data.disk),
                avg_temperature_c: avg(data.temp),
                max_mem_usage_pct: max(data.mem),
                max_cpu_usage_pct: max(data.cpu),
                max_disk_usage_pct: max(data.disk),
                max_temperature_c: max(data.temp),
                data_points: data.dataPoints
              });
            });
            
            // Merge today's buckets with the main data
            finalData = [...response.data, ...todayBuckets];
            
            if (debugAnalytics) {
              console.log('✅ Added current day partial data:', {
                bucketsAdded: todayBuckets.length,
                totalBuckets: finalData.length
              });
            }
          }
        } catch (err: any) {
          // If fetching today's data fails, just use the original data
          console.warn('⚠️ Failed to fetch current day partial data, using original data:', err);
          if (debugAnalytics) {
            console.warn('Error details:', err);
          }
        }
      }
    }
    
    aggregatedMetricsData.value = finalData;
    
    // Fetch player records for all unique UUIDs found in the metrics data
    if (finalData.length > 0) {
      const uniqueDevices = [...new Set(finalData.map(d => d.player_uuid))];
      await fetchPlayerRecordsByUuids(uniqueDevices);
    }
    
    // Debug time range of received data
    if (finalData.length > 0) {
      const timestamps = finalData.map(d => new Date(d.time_bucket));
      const minTime = new Date(Math.min(...timestamps.map(t => t.getTime())));
      const maxTime = new Date(Math.max(...timestamps.map(t => t.getTime())));
      const uniqueDates = [...new Set(finalData.map(d => d.time_bucket.split('T')[0]))].sort();
      const uniqueDevices = [...new Set(finalData.map(d => d.player_uuid))];
      
      if (debugAnalytics) {
        console.log('📊 Aggregated data analysis:');
        console.log('  • Requested range:', { startTime, endTime });
        console.log('  • Actual data range:', {
          earliest: minTime.toISOString(),
          latest: maxTime.toISOString(),
          timeSpanDays: Math.round((maxTime.getTime() - minTime.getTime()) / (1000 * 60 * 60 * 24)),
          totalBuckets: finalData.length,
          interval: response.interval
        });
        console.log('  • Coverage:', {
          uniqueDates: uniqueDates.length,
          uniqueDevices: uniqueDevices.length,
          expectedBuckets: response.totalBuckets,
          coverageRatio: `${Math.round((finalData.length / response.totalBuckets) * 100)}%`
        });
        console.log('  • Devices found:', uniqueDevices.map((uuid: string) => uuid.substring(0, 8) + '...'));
      }
      
      // Debug specific device
      const realtimeTestUuid = '805fb6f4-0a03-4903-93b3-d95ad01f9c8f';
      if (debugAnalytics) {
        const realtimeTestData = finalData.filter(d => d.player_uuid === realtimeTestUuid);
        if (realtimeTestData.length > 0) {
          console.log('🎯 Realtime-logs-test device data found:');
          console.log('  • Total buckets:', realtimeTestData.length);
          console.log('  • Date coverage:', [...new Set(realtimeTestData.map(d => d.time_bucket.split('T')[0]))].sort());
          console.log('  • Sample records:', realtimeTestData.slice(0, 3));
        } else {
          console.log('❌ No data found for realtime-logs-test device');
          console.log('  • All device UUIDs in response:', uniqueDevices);
        }
      }
      
      // Sample data point for debugging
      if (debugAnalytics) console.log('  • Sample aggregated record:', finalData[0]);
    }
    
  } catch (err: any) {
    console.error('❌ Error fetching aggregated metrics data:', err);
    console.error('Error details:', {
      message: err.message,
      response: err.response?.data,
      status: err.response?.status
    });
    metricsError.value = err?.message || 'Failed to fetch metrics data. Please ensure the aggregated metrics API is running on port 3000.';
  } finally {
    isLoadingMetrics.value = false;
  }
};

const fetchUptimeData = async () => {
  if (!startDate.value || !endDate.value) {
    error.value = 'Please select both start and end dates';
    return;
  }

  isLoading.value = true;
  error.value = '';

  try {
    // Calculate startDate - 1 day and endDate + 1 day for fetching
    const start = new Date(startDate.value);
    start.setDate(start.getDate() - 1);
    const fetchStartDate = start.toISOString().split('T')[0];
    
    const end = new Date(endDate.value);
    end.setDate(end.getDate() + 1);
    const fetchEndDate = end.toISOString().split('T')[0];

    if (debugAnalytics) console.log('Calling API with params:', { fetchStartDate, fetchEndDate });
    const data = await getPlayerUptimes(fetchStartDate, fetchEndDate);
    if (debugAnalytics) console.log('Received uptime data:', data);
    uptimeData.value = data;
  } catch (err: any) {
    console.error('Error fetching uptime data:', err);
    console.error('Full error object:', err);
    if (err?.response) {
      console.error('Response status:', err.response.status);
      console.error('Response data:', err.response.data);
    }
    error.value = err?.response?.data?.message || 'Failed to fetch uptime data. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const fetchAllData = async () => {
  // Fetch data sources independently so one failure doesn't affect the other
  const promises = [
    fetchUptimeData().catch(err => {
      console.warn('Uptime data fetch failed:', err);
      // Don't re-throw - let uptime fail gracefully
    }),
    fetchMetricsData().catch(err => {
      console.warn('Metrics data fetch failed:', err);
      // Don't re-throw - let metrics fail gracefully
    })
  ];
  
  await Promise.allSettled(promises);
};

onMounted(() => {
  setDefaultDateRange();
  fetchAllData();
});
</script>

<style scoped>
.analytics-view {
  padding: 0.5rem 1rem 1rem;
  width: 100%;
}

.date-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 120px;
}

.date-input-group label {
  font-weight: 500;
  color: #475569;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.125rem;
}

.date-input-group input {
  padding: 0.375rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.8rem;
  background: white;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.date-input-group input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.date-input-group input:hover {
  border-color: #cbd5e1;
}

.fetch-button {
  padding: 0.5rem 1rem;
  background: #25294a;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(37, 41, 74, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 80px; /* Ensures button width is consistent */
  display: flex;
  align-items: center;
  justify-content: center;
}

.fetch-button:hover:not(:disabled) {
  background: #1e2238;
  box-shadow: 0 4px 8px rgba(37, 41, 74, 0.3);
  transform: translateY(-1px);
}

.fetch-button:disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc2626;
  font-weight: 500;
}

.retry-button {
  padding: 0.5rem 1rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #b91c1c;
}

/* Analytics Grid Layout */
.analytics-grid {
  position: relative;
  min-height: 600px;
}

.loading-overlay,
.error-overlay,
.empty-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  gap: 1rem;
}

.analytics-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Metrics Row */
.metrics-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.metric-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.metric-card h3 {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.date-picker-card .date-controls {
  margin-top: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: end;
  gap: 0.75rem;
}

.date-picker-card .date-input-group {
  min-width: 100px;
}

.date-picker-card .date-input-group label {
  font-size: 0.65rem;
  margin-bottom: 0.125rem;
}

.date-picker-card .date-input-group input {
  padding: 0.25rem 0.375rem;
  font-size: 0.75rem;
}

.date-picker-card .fetch-button {
  padding: 0.25rem 0.375rem;
  font-size: 0.75rem;
  height: 32px; /* Explicit height to match input, adjust if needed */
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Charts Row */
.charts-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1rem;
}

.chart-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-header {
  padding: 1rem 1rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.chart-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.chart-header p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.chart-container {
  padding: 1rem;
  height: 350px; /* Reduced from 450px to match smaller heatmap */
  width: 100%;
}

/* Placeholder Cards */
.chart-card.placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 450px;
}

.placeholder-content {
  text-align: center;
  color: #6b7280;
}

.placeholder-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.placeholder-content p {
  font-size: 0.875rem;
  margin: 0;
}

/* Recently Offline Devices */
.offline-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.offline-list li {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.375rem 0.25rem;
  border-bottom: 1px dashed #e5e7eb;
}
.offline-link {
  color: #3b82f6;
  text-decoration: underline;
}
.offline-meta {
  color: #6b7280;
  font-size: 0.8rem;
}
.no-offline {
  color: #6b7280;
}

/* General list card container without fixed height */
.list-card-container {
  padding: 1rem;
}

/* Scroll wrapper to keep card compact */
.offline-list-scroll {
  max-height: 360px;
  overflow-y: auto;
}

/* Thin scrollbar */
.offline-list-scroll::-webkit-scrollbar {
  width: 6px;
}
.offline-list-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

/* No data states */
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #6b7280;
  text-align: center;
  padding: 2rem;
}

.no-data-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-data-container p {
  margin: 0 0 0.5rem 0;
  font-weight: 500;
}

.no-data-container small {
  color: #9ca3af;
  font-size: 0.8rem;
  line-height: 1.4;
}

/* Responsive Design */
@media (max-width: 768px) {
  .analytics-view {
    padding: 0.5rem;
  }
  
  .date-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .metrics-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .charts-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .chart-container {
    height: 250px; /* Reduced for mobile */
  }

  .no-data-container {
    height: 250px;
    padding: 1rem;
  }

  .no-data-icon {
    font-size: 2rem;
  }
}

.access-denied {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  color: #6b7280;
}

.access-denied h2 {
  color: #dc2626;
  margin-bottom: 1rem;
}

.no-data-message {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-style: italic;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  min-height: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.inline-spinner {
  display: inline-block;
  vertical-align: middle;
  /* Remove margin-right since there's no text */
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-overlay {
  display: none;
}
</style> 